import * as hardware from "@client/hardware";
import {clamp, motion, useMotionValue, useSpring} from "motion/react";
import React from "react";

// Swipe a row left to delete it, after Motion's Swipe actions
// (https://examples.motion.dev/react/swipe-actions). The row follows the finger
// and Delete comes in from the right edge. A swipe past one button width snaps
// the row open that wide; past COMMIT of the row the red fills to the edge, and
// letting go there deletes.

const SPRING = {stiffness: 900, damping: 80};
const BUTTON = 80;
const COMMIT = 0.8;
// Movement under this is a tap, and a mostly vertical one is a scroll.
const SLOP = 8;

// One row is open at a time: opening a row closes the last one.
let closeOpenRow: (() => void) | null = null;

export const SwipeToDelete = (props: {
    disabled: boolean;
    onDelete: () => void;
    // Presses here never start a swipe (the sort handle).
    ignore: string;
    children: React.ReactNode;
}) => {
    const row = React.useRef<HTMLDivElement>(null);
    const offset = useMotionValue(0);
    const x = useSpring(offset, SPRING);

    // An open row closes on a press anywhere else.
    const outside = React.useRef<((event: PointerEvent) => void) | null>(null);

    const close = React.useCallback(() => {
        offset.set(0);
        if (closeOpenRow === close) {
            closeOpenRow = null;
        }
        if (outside.current != null) {
            document.removeEventListener("pointerdown", outside.current, true);
            outside.current = null;
        }
    }, [offset]);

    const open = () => {
        offset.set(-BUTTON);
        closeOpenRow = close;
        if (outside.current == null) {
            outside.current = (event: PointerEvent) => {
                if (row.current?.contains(event.target as Node) !== true) {
                    close();
                }
            };
            document.addEventListener("pointerdown", outside.current, true);
        }
    };

    React.useEffect(() => close, [close]);

    const handlePointerDown = (event: React.PointerEvent) => {
        if (
            props.disabled ||
            event.button !== 0 ||
            (event.target as Element).closest(props.ignore) != null
        ) {
            return;
        }
        const width = row.current!.getBoundingClientRect().width;
        const startX = event.clientX;
        const startY = event.clientY;
        const startOffset = offset.get();
        let swiping = false;
        let committed = false;

        const move = (moved: PointerEvent) => {
            const dx = moved.clientX - startX;
            if (!swiping) {
                if (
                    Math.abs(dx) < SLOP ||
                    Math.abs(dx) < Math.abs(moved.clientY - startY)
                ) {
                    return;
                }
                swiping = true;
                if (closeOpenRow !== close) {
                    closeOpenRow?.();
                }
                // A mouse press focuses the field it lands on; a swipe is not an edit.
                if (row.current!.contains(document.activeElement)) {
                    (document.activeElement as HTMLElement).blur();
                }
            }
            const next = clamp(-width, 0, startOffset + dx);
            const past = next < -width * COMMIT;
            if (past !== committed) {
                committed = past;
                if (past) {
                    void hardware.Haptics.impact({style: hardware.ImpactStyle.Heavy});
                }
            }
            offset.set(past ? -width : next);
        };

        const end = (ended: PointerEvent) => {
            document.removeEventListener("pointermove", move);
            document.removeEventListener("pointerup", end);
            document.removeEventListener("pointercancel", end);
            if (!swiping) {
                return;
            }
            // A mouse release clicks whatever the face carried under it. That click
            // comes in this task; touch sends none after a move.
            const swallow = (click: MouseEvent) => click.stopPropagation();
            window.addEventListener("click", swallow, {capture: true, once: true});
            setTimeout(() => window.removeEventListener("click", swallow, true));
            if (committed && ended.type === "pointerup") {
                props.onDelete();
            } else if (offset.get() < -BUTTON) {
                open();
            } else {
                close();
            }
        };

        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", end);
        document.addEventListener("pointercancel", end);
    };

    return (
        <div
            ref={row}
            className="relative touch-pan-y overflow-x-clip"
            onPointerDown={handlePointerDown}
        >
            <motion.div className="relative z-6" style={{x}}>
                {props.children}
            </motion.div>
            <motion.div className="absolute top-0 left-full h-full w-full" style={{x}}>
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={props.onDelete}
                    className="flex h-full w-full items-center bg-rose-600 text-xs font-semibold text-white lowercase"
                >
                    <span className="text-center" style={{width: BUTTON}}>
                        Delete
                    </span>
                </button>
            </motion.div>
        </div>
    );
};
